import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Loader } from '../../components/common';

import Auth from '../../amplify';
import { Flex, Text, Effects } from '../../components/common';
import ResetPasswordModal from './reset.password.modal';
import SignupModal from './signup.modal';
import styles from './styles.scss';

const initialState = {
  username: '',
  password: '',
  newPassword: '',
  errorMessage: null,
  showResetPassword: false,
  fetching: true,
  showPlaintext: false,
  showSignupModal: false,
  loadedSession: false,
};
export default function Login({ syncUserData, signup }) {
  const [form, setForm] = useState(initialState);
  const updateState = (changes) => setForm({ ...form, ...changes });
  const { width } = Effects.useDimensions();

  const login = (password) => {
    updateState({ fetching: true });
    Auth.signIn(form.username.toLowerCase(), password || form.password)
      .then((session) => fetchSessionUser(session))
      .catch((e) => updateState({ errorMessage: e.message, fetching: false }));
  };

  const catchError = () => {
    Auth.signOut();
    updateState({ fetching: false, loadedSession: true });
  };

  const fetchSessionUser = (session) => {
    const username = session.username || session.idToken.payload.sub;
    return syncUserData(username)
      .then((user) => {
        if (!user) {
          throw new Error('uh oh!');
        }
      })
      .catch(catchError);
  };

  useEffect(() => {
    Auth.currentSession()
      .then((session) => {
        if (session && session.idToken) {
          return fetchSessionUser(session);
        }
        updateState({ fetching: false, loadedSession: true });
      })
      .catch(catchError);
  }, []);

  let body = <Loader />;
  let leftColumn = null;
  if (width > 900 && form.loadedSession) {
    leftColumn = <div className={styles.leftColumn} />;
  }
  if (!form.fetching) {
    body = (
      <div className={styles.loginForm}>
        <div alt="logo" className={styles.logoImage} />
        <div className={styles.loginInfo}>
          Welcome to your online Alice account.
          <br />
          Login or create your account below!
        </div>
        <div className={styles.formField}>
          <i className="fas fa-user"></i>
          <input
            className={styles.formField}
            type="email"
            name="username"
            id="username"
            placeholder="Username"
            value={form.username}
            onChange={(e) => updateState({ username: e.target.value })}
          />
        </div>
        <div className={styles.formField}>
          <i className="fas fa-lock"></i>
          <input
            className={styles.formField}
            type={form.showPlaintext ? 'text' : 'password'}
            name="password"
            id="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => updateState({ password: e.target.value })}
          />
        </div>
        {form.errorMessage && (
          <Text isError mb={5}>
            {form.errorMessage}
          </Text>
        )}
        <Button
          secondary
          onClick={() => updateState({ showPlaintext: !form.showPlaintext })}
          type="button"
        >
          {form.showPlaintext ? 'Hide Password' : 'Show Password'}
        </Button>
        <Button
          mt={10}
          submit
          stretch
          value="Login"
          disabled={form.password.length < 8 || !form.username}
          onClick={() => login()}
        >
          Login
        </Button>
        <Button
          mt={10}
          primary
          stretch
          onClick={() => updateState({ showSignupModal: true })}
          type="button"
        >
          Create Account
        </Button>
        <Button
          mt={10}
          secondary
          stretch
          onClick={() => updateState({ showResetPassword: true })}
          type="button"
        >
          Reset Password
        </Button>
      </div>
    );
  }
  return (
    <Flex
      className={classNames(styles.container, {
        [styles.loading]: form.fetching,
      })}
      growToContainer
      justifyContent="center"
      alignItems="stretch"
    >
      {leftColumn}
      <Flex
        style={{ flex: 1, minWidth: '40%' }}
        alignItems="center"
        justifyContent="center"
      >
        {body}
      </Flex>
      <ResetPasswordModal
        isVisible={form.showResetPassword}
        onClose={() => updateState({ showResetPassword: false })}
      />
      <SignupModal
        isVisible={form.showSignupModal}
        onClose={() => updateState({ showSignupModal: false })}
        signup={signup}
      />
    </Flex>
  );
}

Login.propTypes = {
  syncUserData: PropTypes.func.isRequired,
};
